/**
 * API Client Configuration
 *
 * The backend exposes one Azure Function per REST endpoint, so this module
 * is now a thin axios wrapper. There is no URL→action translation; callers
 * pass the path they want (e.g. "/individuals/123") and we issue a real
 * HTTP method against the configured base URL.
 */
import axios, { AxiosInstance, AxiosError, AxiosResponse } from 'axios';
import { ApiResponse } from '../types/api';

// Get API base URL from environment variable or use default
// Supports either AZURE_FUNCTION_URL or VITE_API_BASE_URL
const API_BASE_URL =
  (import.meta as any).env?.AZURE_FUNCTION_URL ||
  (import.meta as any).env?.VITE_API_BASE_URL ||
  'http://localhost:7071/api';

// Get Azure Function key from environment variable (required for production)
const AZURE_FUNCTION_KEY = (import.meta as any).env?.VITE_AZURE_FUNCTION_KEY || '';

/**
 * Create axios instance with default configuration
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor — attach auth + function key headers.
 */
apiClient.interceptors.request.use(
  (config) => {
    if (AZURE_FUNCTION_KEY) {
      config.headers['x-functions-key'] = AZURE_FUNCTION_KEY;
    }

    const token = localStorage.getItem('tacolcy_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (import.meta.env?.DEV) {
      console.log(`→ ${config.method?.toUpperCase()} ${config.url}`);
    }

    return config;
  },
  (error) => {
    console.error('⚠️ Request error:', error);
    return Promise.reject(error);
  }
);

/**
 * Response interceptor — auth failure handling + structured error logging.
 */
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    if (import.meta.env?.DEV) {
      console.log(
        `✓ ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`
      );
    }
    return response;
  },
  (error: AxiosError) => {
    const errorContext = {
      timestamp: new Date().toISOString(),
      endpoint: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      status: error.response?.status,
      statusText: error.response?.statusText,
      errorData: error.response?.data,
    };

    if (error.response) {
      const status = error.response.status;

      if (status === 401) {
        console.error('🔒 Unauthorized access:', errorContext);
        try {
          localStorage.removeItem('tacolcy_token');
          localStorage.removeItem('tacolcy_user');
        } catch {
          // Storage access may fail in restricted environments
        }
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      } else if (status === 403) {
        console.error('🚫 Forbidden access:', errorContext);
      } else if (status === 500) {
        console.error('💥 Internal server error:', errorContext);
      } else if (status === 503) {
        console.warn('⏳ Service temporarily unavailable:', errorContext);
      } else {
        console.error(`❌ API Error (${status}):`, errorContext);
      }
    } else if (error.request) {
      console.error('📡 No response from server:', {
        ...errorContext,
        baseURL: apiClient.defaults.baseURL,
      });
    } else {
      console.error('⚠️ Request setup error:', errorContext);
    }

    return Promise.reject(error);
  }
);

/**
 * Generic API request — direct REST passthrough.
 *
 * The backend deploys each route as its own Azure Function, so we issue the
 * request method and URL straight at the Function App. Query params and body
 * map to axios's standard shapes.
 */
async function apiRequest<T>(
  method: 'get' | 'post' | 'put' | 'delete',
  url: string,
  data?: any,
  params?: any
): Promise<ApiResponse<T>> {
  try {
    const response = await apiClient.request<ApiResponse<T>>({
      method,
      url,
      data,
      params,
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return error.response.data as ApiResponse<T>;
    }

    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: 'Network error occurred. Please try again.',
      },
    };
  }
}

/**
 * GET request
 */
export async function apiGet<T>(url: string, params?: any): Promise<ApiResponse<T>> {
  return apiRequest<T>('get', url, undefined, params);
}

/**
 * POST request
 */
export async function apiPost<T>(url: string, data?: any): Promise<ApiResponse<T>> {
  return apiRequest<T>('post', url, data);
}

/**
 * PUT request
 */
export async function apiPut<T>(url: string, data?: any): Promise<ApiResponse<T>> {
  return apiRequest<T>('put', url, data);
}

/**
 * DELETE request
 */
export async function apiDelete<T>(url: string): Promise<ApiResponse<T>> {
  return apiRequest<T>('delete', url);
}

/**
 * Build query string from params (kept for callers that build URLs manually).
 */
export function buildQueryString(params: Record<string, any>): string {
  const queryParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryParams.append(key, String(value));
    }
  });

  const queryString = queryParams.toString();
  return queryString ? `?${queryString}` : '';
}

export default apiClient;
